import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Button, Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import Modal from "../Modal";
import { UploadButton } from "../Buttons";
import ProfilePictureWithCropper from "./ProfilePictureWithCropper";
import DefaultProfileImage from "./DefaultProfileImage";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  applyButton: {
    marginLeft: theme.spacing(3)
  },
  profileImage: {
    margin: "auto",
    display: "block"
  }
}));

function getInitial(name) {
  return name ? name.trim().charAt(0) : "";
}

export default function ProfilePictureModal(props) {
  const {
    onApply,
    onClose,
    open,
    profilePictureContentType,
    firstName,
    lastName,
    profilePicture
  } = props;
  const classes = useStyles();
  const [newPicture, setNewPicture] = React.useState(null);
  const [profilePictureBlob, setProfilePictureBlob] = React.useState(null);

  const handleImageUpload = event => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        () => {
          setNewPicture(reader.result);
        },
        false
      );
      reader.readAsDataURL(event.currentTarget.files[0]);
    }
  };

  const onProfilePictureBlobChange = blob => {
    setProfilePictureBlob(blob);
  };

  function handleCloseModal(event) {
    onClose();
  }

  const handleApplyProfileImage = event => {
    event.preventDefault();
    if (profilePictureBlob) {
      try {
        let profilePictureFile = new File(
          [profilePictureBlob],
          "profile_picture.jpeg",
          { mime: "image/jpeg" }
        );
        onApply(profilePictureFile, function() {
          onClose();
        });
      } catch (err) {
        let profilePictureFile = new Blob(
          [profilePictureBlob],
          "profile_picture.jpeg",
          { mime: "image/jpeg" }
        );
        onApply(profilePictureFile, function() {
          onClose();
        });
      }
    }
  };

  function getDialogContent() {
    if (newPicture) {
      return (
        <ProfilePictureWithCropper
          profilePicture={newPicture}
          onProfilePictureBlobChange={onProfilePictureBlobChange}
          profilePictureContentType={profilePictureContentType}
        />
      );
    } else {
      if (profilePicture) {
        return (
          <img
            src={profilePicture}
            alt="Profile"
            className={classes.profileImage}
          />
        );
      } else {
        return (
          <Box>
            <DefaultProfileImage
              firstInitial={getInitial(firstName)}
              lastInitial={getInitial(lastName)}
            />
          </Box>
        );
      }
    }
  }

  function getDialogTitle() {
    return <Typography variant="h4">Edit Profile Photo</Typography>;
  }

  function getDialogActions() {
    const { t } = useTranslation();
    return (
      <Box display="flex" justifyContent="space-between" width="100%" mt={1}>
        <UploadButton
          labelVariant="inherit"
          buttonVariant="text"
          buttonText="Upload Photo"
          acceptType="image/*"
          onUpload={handleImageUpload}
          buttonId="upload-profile-image-button"
        />
        <Box display="flex">
          <Button variant="text" color="primary" onClick={handleCloseModal}>
            {t("cancelBtn")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.applyButton}
            onClick={handleApplyProfileImage}
          >
            {t("applyBtn")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Modal
      maxWidth="xs"
      dialogTitle={getDialogTitle()}
      dialogContent={getDialogContent()}
      dialogActions={getDialogActions()}
      open={open}
      onClose={onClose}
    />
  );
}

ProfilePictureModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  profilePicture: PropTypes.any
};
